import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";

import { cn } from "../lib/utils";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";
import { getZar8YieldPerformanceDelta } from "../utils/zar8YieldOverview";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

const Y_AXIS_LABELS = [
  "3.0k",
  "2.5k",
  "2.0k",
  "1.5k",
  "1.0k",
  "500",
  "0",
  "-500",
];

const getDefaultActivePointIndex = (length) =>
  length > 4 ? 4 : Math.max(length - 1, 0);

function ChartState({ title, body }) {
  return (
    <div className="flex h-[236px] items-center justify-center rounded-2xl border border-dashed border-[#EBE9EF] bg-[#FAF9FC] px-6 text-center">
      <div>
        <p className="text-base font-semibold leading-6 text-[#423E51]">
          {title}
        </p>
        <p className="mt-2 text-sm leading-5 text-[#6D6584]">{body}</p>
      </div>
    </div>
  );
}

export default function Zar8YieldOverviewChart({
  series = [],
  totalLockedValue = 0,
  isLoading = false,
  hasError = false,
  onViewMore,
  viewMoreDisabled = false,
  isPreview = false,
}) {
  const displaySeries = series.length > 0 ? series : [];
  const [activePointIndex, setActivePointIndex] = useState(() =>
    getDefaultActivePointIndex(displaySeries.length)
  );
  const labels = displaySeries.map((point) => point.shortLabel);
  const values = displaySeries.map((point) => point.value);
  const activePoint = displaySeries.at(activePointIndex) || null;
  const { deltaValue, deltaPercentage } =
    getZar8YieldPerformanceDelta(displaySeries);

  useEffect(() => {
    setActivePointIndex(getDefaultActivePointIndex(displaySeries.length));
  }, [displaySeries.length]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          fill: true,
          borderColor: "#57249C",
          backgroundColor: (context) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return "rgba(87, 36, 156, 0.2)";
            }

            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, "rgba(87, 36, 156, 0.35)");
            gradient.addColorStop(1, "rgba(87, 36, 156, 0)");
            return gradient;
          },
          tension: 0,
          borderWidth: 2,
          pointRadius: (context) =>
            context.dataIndex === activePointIndex ? 5 : 0,
          pointHoverRadius: (context) =>
            context.dataIndex === activePointIndex ? 5 : 0,
          pointBackgroundColor: "#EB3366",
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointHitRadius: 12,
        },
      ],
    }),
    [activePointIndex, labels, values]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: {
        padding: 0,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          display: false,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          min: -500,
          max: 3000,
          display: false,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
      onHover: (_, elements) => {
        if (elements.length > 0) {
          setActivePointIndex(elements[0].index);
        }
      },
      elements: {
        line: {
          capBezierPoints: false,
        },
      },
    }),
    []
  );

  return (
    <section
      className={cn(
        "relative rounded-[20px] border border-[#F1EDF7] bg-white px-6 py-8 xl:h-[327px] xl:overflow-hidden",
        isPreview ? "xl:w-[540px]" : "xl:w-auto"
      )}
    >
      <p className="absolute left-[21px] top-[25px] text-[24px] font-semibold leading-7 text-[#57249C]">
        Performance
      </p>

      <button
        type="button"
        onClick={onViewMore}
        disabled={viewMoreDisabled}
        className={cn(
          "absolute left-[2px] top-[45px] flex h-12 items-center justify-center rounded-full px-6 text-[14px] font-semibold leading-5 text-[#EB3366]",
          viewMoreDisabled && "cursor-not-allowed opacity-40"
        )}
      >
        View more
      </button>

      <div className="absolute right-6 top-[21px] flex h-16 flex-col items-end justify-between">
        <p className="text-right text-[10.894px] leading-[12.709px] text-[#6D6584]">
          Total Locked
        </p>
        <p className="text-[32px] font-bold leading-8 tracking-[-1.28px] text-[#57249C]">
          {formatFiatWithTwoDecimals(totalLockedValue)}
        </p>
        <p
          className={cn(
            "text-[14px] font-semibold leading-5",
            deltaValue >= 0 ? "text-[#17C3A5]" : "text-[#DA2F58]"
          )}
        >
          {deltaValue >= 0 ? "+" : "-"}{" "}
          {formatFiatWithTwoDecimals(Math.abs(deltaValue))} (
          {Math.abs(deltaPercentage).toFixed(0)}%)
        </p>
      </div>

      <div className="absolute inset-x-5 bottom-5 top-[66px]">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-5 w-32 rounded bg-[#F1EDF7]" />
            <div className="h-[236px] rounded-2xl bg-[#F8F6FB]" />
          </div>
        ) : null}

        {!isLoading && hasError ? (
          <ChartState
            title="Performance unavailable"
            body="We couldn’t load your yield performance right now. Try again in a moment."
          />
        ) : null}

        {!isLoading && !hasError && displaySeries.length === 0 ? (
          <ChartState
            title="No performance history yet"
            body="Performance data will appear here once your active positions start generating history."
          />
        ) : null}

        {!isLoading && !hasError && displaySeries.length > 0 ? (
          <div className="relative h-full">
            <div className="absolute left-0 top-[34px] flex h-[172px] w-[24px] flex-col justify-between text-right text-[10.894px] leading-[12.709px] text-[#6D6584]">
              {Y_AXIS_LABELS.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>

            <div
              className="absolute bottom-[36px] left-[44px] right-[22px] top-[24px]"
              onMouseLeave={() =>
                setActivePointIndex(
                  getDefaultActivePointIndex(displaySeries.length)
                )
              }
            >
              <div className="absolute inset-0 grid grid-cols-8 grid-rows-8">
                {Array.from({ length: 64 }).map((_, index) => (
                  <div
                    key={index}
                    className="border-r border-t border-[#F1EDF7] last:border-r-0"
                  />
                ))}
              </div>

              <div className="absolute inset-0">
                <Line data={data} options={options} />
              </div>

              {activePoint ? (
                <>
                  <div
                    className="absolute top-0 h-[154px] border-l-2 border-dashed border-[#57249C]"
                    style={{
                      left: `${(activePointIndex / Math.max(displaySeries.length - 1, 1)) * 100}%`,
                      transform: "translateX(-1px)",
                    }}
                  />
                  <div
                    className="absolute rounded-[7.263px] bg-[#57249C] px-[7.263px] py-[2px] text-[10.894px] leading-[12.709px] text-white"
                    style={{
                      left: `${(activePointIndex / Math.max(displaySeries.length - 1, 1)) * 100}%`,
                      top: "0px",
                      transform: "translate(-50%, 0)",
                    }}
                  >
                    <span>
                      {activePoint.fullLabel.replace("2025", "").trim()}
                    </span>{" "}
                    <span className="font-bold">
                      {formatFiatWithTwoDecimals(activePoint.value).replace(
                        " ",
                        ""
                      )}
                    </span>
                  </div>
                </>
              ) : null}
            </div>

            <div className="absolute bottom-0 left-[44px] right-[22px] flex items-start justify-between text-center text-[10.894px] leading-[12.709px] text-[#6D6584]">
              {labels.map((label, index) => (
                <span
                  key={`${label}-${index}`}
                  className={
                    index === activePointIndex ? "font-bold text-[#423E51]" : ""
                  }
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
